import {
  CarOutlined,
  EnvironmentOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DropboxOutlined,
  InboxOutlined,
  RollbackOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Tag, Tooltip } from "antd";
import type { OrderStatus, Schemas } from "@/api/client";
import { useI18n } from "@/i18n";

/** Statuses in lifecycle order; the order list's status filter follows it. */
export const ORDER_STATUSES: OrderStatus[] = ["new", "processing", "packed", "shipped", "delivered", "completed", "cancelled", "returned", "awaiting_payment"];

// Status is always icon + label, never colour alone (Design Brief §2.4).
const look: Record<OrderStatus, { color: string; icon: React.ReactNode }> = {
  awaiting_payment: { color: "default", icon: <ClockCircleOutlined /> },
  new: { color: "blue", icon: <InboxOutlined /> },
  processing: { color: "processing", icon: <SyncOutlined /> },
  packed: { color: "cyan", icon: <DropboxOutlined /> },
  shipped: { color: "geekblue", icon: <CarOutlined /> },
  delivered: { color: "purple", icon: <EnvironmentOutlined /> },
  completed: { color: "success", icon: <CheckCircleOutlined /> },
  cancelled: { color: "default", icon: <CloseCircleOutlined /> },
  returned: { color: "warning", icon: <RollbackOutlined /> },
};

export function OrderStatusTag({ status }: { status: OrderStatus }) {
  const { t } = useI18n();
  const l = look[status];
  return (
    <Tag color={l.color} icon={l.icon}>
      {t(`status.${status}`)}
    </Tag>
  );
}

export type OrderHold = Schemas["OrderHold"];
export const ORDER_HOLDS: OrderHold[] = ["oos", "unmapped", "no_warehouse", "cancel_request", "rule", "manual", "arrange_failed", "quota", "unpaid", "combined"];

const holdColor: Record<OrderHold, string> = {
  oos: "error",
  unmapped: "warning",
  no_warehouse: "warning",
  cancel_request: "error",
  rule: "gold",
  manual: "gold",
  arrange_failed: "volcano",
  quota: "purple",
  unpaid: "gold",
  combined: "purple",
};

/**
 * A held order waiting as Baru or Diproses is not that work yet: it shows as
 * held, with the reason, in place of its status.
 */
export function OrderStateTag({ o }: { o: Pick<Schemas["OrderSummary"], "status" | "hold" | "holdNote"> }) {
  return o.hold && (o.status === "new" || o.status === "processing") ? <HoldTag hold={o.hold} note={o.holdNote} /> : <OrderStatusTag status={o.status} />;
}

/** Why a paid order does not go on (Plan §6.11), with what holds it on hover. */
export function HoldTag({ hold, note }: { hold: OrderHold; note?: string }) {
  const { t } = useI18n();
  const tag = (
    <Tag color={holdColor[hold]} icon={<PauseCircleOutlined />}>
      {t(`orders.hold.${hold}`)}
    </Tag>
  );
  return note ? <Tooltip title={note}>{tag}</Tooltip> : tag;
}

/**
 * The marketplace's own status in words (e.g. TikTok IN_TRANSIT: "In
 * transit"), beside Zerpid's coarser one. Unknown codes show as sent.
 */
export function usePlatformStatus() {
  const { tDynamic } = useI18n();
  // Lazada and Blibli join the statuses of an order's items with "+".
  return (raw: string) =>
    raw
      .split("+")
      .filter(Boolean)
      .map((part) => tDynamic(`mpStatus.${part.toUpperCase()}`, "mpStatus.other", { status: part }))
      .join(" + ");
}
